import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, Calendar, Clock, User, DollarSign, CheckCircle, Circle, Trash2, Edit3, X, ChevronLeft, ChevronRight, Smartphone, PenLine, TrendingUp, Wallet, ChevronDown, ChevronUp, Repeat } from 'lucide-react';
import { format, addDays, startOfWeek, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HOURS = ['07:00','08:00','09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','17:00','18:00','19:00','20:00','21:00'];
const LESSON_TYPES = [
  { value: 1, label: 'Singola', defaultPrice: 30 },
  { value: 2, label: 'Doppia', defaultPrice: 40 },
  { value: 3, label: 'Tripla', defaultPrice: 45 },
  { value: 4, label: 'In 4', defaultPrice: 60 },
];

const GestionaleLessonsPage = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('day'); // day, week
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showEarnings, setShowEarnings] = useState(false);
  const [earnings, setEarnings] = useState(null);
  const [formData, setFormData] = useState({
    player_name: '', date: '', time: '09:00', num_players: 1, price: 30, notes: '', is_recurring: false, recurring_weeks: 4
  });

  const getDateRange = useCallback(() => {
    if (viewMode === 'day') {
      const d = format(selectedDate, 'yyyy-MM-dd');
      return { from: d, to: d };
    }
    const start = startOfWeek(selectedDate, { weekStartsOn: 1 });
    return {
      from: format(start, 'yyyy-MM-dd'),
      to: format(addDays(start, 6), 'yyyy-MM-dd')
    };
  }, [selectedDate, viewMode]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { from, to } = getDateRange();
      const res = await axios.get(`${API_URL}/api/admin/lessons/gestionale?date_from=${from}&date_to=${to}`, { withCredentials: true });
      setBookings(res.data.bookings);
      setSummary(res.data.summary);
    } catch (error) {
      if (error.response?.status === 403) navigate('/dashboard');
      else toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  }, [getDateRange, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchEarnings = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/lessons/earnings`, { withCredentials: true });
      setEarnings(res.data);
    } catch (error) {
      console.error('Error fetching earnings:', error);
    }
  };

  useEffect(() => { fetchEarnings(); }, []);

  const navigate_date = (dir) => {
    const days = viewMode === 'day' ? 1 : 7;
    setSelectedDate(prev => addDays(prev, dir * days));
  };

  const openAddForm = (date, time) => {
    setEditingId(null);
    setFormData({
      player_name: '',
      date: date || format(selectedDate, 'yyyy-MM-dd'),
      time: time || '09:00',
      num_players: 1,
      price: 30,
      notes: '',
      is_recurring: false,
      recurring_weeks: 4
    });
    setShowAddForm(true);
  };

  const openEditForm = (booking) => {
    setEditingId(booking.booking_id);
    setFormData({
      player_name: booking.player_name || '',
      date: booking.date,
      time: booking.time,
      num_players: booking.num_players || 1,
      price: booking.price || 30,
      notes: booking.notes || '',
      is_recurring: false,
      recurring_weeks: 4
    });
    setShowAddForm(true);
  };

  const handleSubmit = async () => {
    if (!formData.player_name.trim()) {
      toast.error('Inserisci il nome dell\'allievo');
      return;
    }
    try {
      if (editingId) {
        await axios.put(`${API_URL}/api/admin/lessons/${editingId}/edit`, formData, { withCredentials: true });
        toast.success('Lezione aggiornata');
      } else {
        const res = await axios.post(`${API_URL}/api/admin/lessons/manual`, formData, { withCredentials: true });
        if (formData.is_recurring && res.data.count) {
          toast.success(`Create ${res.data.count} lezioni settimanali`);
        } else {
          toast.success('Lezione aggiunta');
        }
      }
      setShowAddForm(false);
      fetchData();
      fetchEarnings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const togglePaid = async (booking) => {
    try {
      await axios.put(`${API_URL}/api/admin/lessons/${booking.booking_id}/paid`,
        { is_paid: !booking.is_paid }, { withCredentials: true });
      fetchData();
      fetchEarnings();
    } catch { toast.error('Errore'); }
  };

  const toggleCompleted = async (booking) => {
    try {
      await axios.put(`${API_URL}/api/admin/lessons/${booking.booking_id}/completed`,
        { is_completed: !booking.is_completed }, { withCredentials: true });
      fetchData();
      fetchEarnings();
    } catch { toast.error('Errore'); }
  };

  const deleteLesson = async (booking) => {
    if (booking.is_recurring && booking.recurring_group_id) {
      const choice = window.confirm('Vuoi eliminare anche tutte le lezioni future di questo gruppo ricorrente?\n\nOK = Elimina tutte le future\nAnnulla = Elimina solo questa');
      if (choice) {
        try {
          const res = await axios.delete(`${API_URL}/api/admin/lessons/recurring/${booking.recurring_group_id}`, { withCredentials: true });
          toast.success(res.data.message);
          fetchData();
          fetchEarnings();
          return;
        } catch { toast.error('Errore'); return; }
      }
    }
    if (!window.confirm(`Eliminare la lezione di ${booking.player_name}?`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/lessons/${booking.booking_id}/delete`, { withCredentials: true });
      toast.success('Lezione eliminata');
      fetchData();
      fetchEarnings();
    } catch { toast.error('Errore'); }
  };

  const groupByDate = () => {
    const groups = {};
    bookings.forEach(b => {
      if (!groups[b.date]) groups[b.date] = [];
      groups[b.date].push(b);
    });
    return groups;
  };

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'EEEE d MMMM', { locale: it });
    } catch { return dateStr; }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-br from-teal-600 to-emerald-700 px-4 py-5 pb-6">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => navigate('/admin')} data-testid="back-admin" className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center">
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <div>
            <h1 className="font-outfit text-xl font-bold text-white">Gestionale Lezioni</h1>
            <p className="text-emerald-100 text-xs">Le tue lezioni del giorno</p>
          </div>
        </div>

        {/* Date navigation */}
        <div className="flex items-center justify-between bg-white/10 rounded-2xl p-2">
          <button onClick={() => navigate_date(-1)} data-testid="prev-date" className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-white/10">
            <ChevronLeft className="w-5 h-5 text-white" />
          </button>
          <div className="text-center">
            <p className="text-white font-semibold text-sm capitalize">
              {viewMode === 'day'
                ? format(selectedDate, 'EEEE d MMMM yyyy', { locale: it })
                : `${format(startOfWeek(selectedDate, {weekStartsOn: 1}), 'd MMM', {locale: it})} — ${format(addDays(startOfWeek(selectedDate, {weekStartsOn: 1}), 6), 'd MMM yyyy', {locale: it})}`
              }
            </p>
          </div>
          <button onClick={() => navigate_date(1)} data-testid="next-date" className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-white/10">
            <ChevronRight className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* View toggle */}
        <div className="flex gap-2 mt-3">
          {['day', 'week'].map(mode => (
            <button key={mode} onClick={() => setViewMode(mode)} data-testid={`view-${mode}`}
              className={`flex-1 py-2 rounded-xl text-xs font-semibold transition-all ${viewMode === mode ? 'bg-white text-teal-700' : 'text-white/70 hover:bg-white/10'}`}>
              {mode === 'day' ? 'Giorno' : 'Settimana'}
            </button>
          ))}
          <button onClick={() => setSelectedDate(new Date())} className="px-3 py-2 rounded-xl text-xs font-semibold text-white/70 hover:bg-white/10">
            Oggi
          </button>
        </div>
      </div>

      {/* Summary bar */}
      {summary && (
        <div className="px-4 -mt-3">
          <div className="bg-white rounded-2xl p-3 shadow-sm border border-slate-100 grid grid-cols-3 gap-2" data-testid="lesson-summary">
            <div className="text-center">
              <p className="text-lg font-bold text-slate-900">{summary.total_lessons}</p>
              <p className="text-[10px] text-slate-500">Lezioni</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-emerald-600">{summary.paid_revenue}€</p>
              <p className="text-[10px] text-emerald-500">Incassato</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-amber-600">{summary.unpaid_revenue}€</p>
              <p className="text-[10px] text-amber-500">Da incassare</p>
            </div>
          </div>
        </div>
      )}

      {/* Earnings Panel */}
      <div className="px-4 mt-3">
        <button
          onClick={() => setShowEarnings(!showEarnings)}
          data-testid="toggle-earnings"
          className="w-full bg-white rounded-2xl p-3.5 shadow-sm border border-slate-100 flex items-center justify-between hover:bg-slate-50 transition-colors"
        >
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-emerald-100 rounded-xl flex items-center justify-center">
              <Wallet className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold text-slate-900">I Miei Guadagni</p>
              <p className="text-xs text-slate-400">Il tuo 50% sulle lezioni</p>
            </div>
          </div>
          {showEarnings ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
        </button>

        {showEarnings && earnings && (
          <div className="mt-2 space-y-3 animate-fadeIn" data-testid="earnings-panel">
            {/* Period cards */}
            <div className="grid grid-cols-2 gap-2">
              <EarningsCard label="Oggi" data={earnings.today} accent="sky" />
              <EarningsCard label="Settimana" data={earnings.week} accent="purple" />
              <EarningsCard label="Mese" data={earnings.month} accent="emerald" />
              <EarningsCard label="Totale" data={earnings.all_time} accent="amber" />
            </div>

            {/* Monthly breakdown */}
            {earnings.monthly_breakdown?.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Dettaglio Mensile</p>
                </div>
                <div className="divide-y divide-slate-50">
                  {earnings.monthly_breakdown.map(m => {
                    const [year, month] = m.month.split('-');
                    const monthNames = ['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'];
                    const label = `${monthNames[parseInt(month) - 1]} ${year}`;
                    return (
                      <div key={m.month} className="px-4 py-3 flex items-center justify-between" data-testid={`earning-month-${m.month}`}>
                        <div>
                          <p className="text-sm font-medium text-slate-900">{label}</p>
                          <p className="text-xs text-slate-400">{m.lessons} {m.lessons === 1 ? 'lezione' : 'lezioni'}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-emerald-600">{m.my_share}€</p>
                          <p className="text-[10px] text-slate-400">su {m.total}€</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="px-4 pt-4 pb-24 space-y-4">
        {loading ? (
          <div className="space-y-3">
            {[1,2,3].map(i => <div key={i} className="h-20 bg-white rounded-2xl animate-pulse" />)}
          </div>
        ) : bookings.length === 0 ? (
          <div className="bg-white rounded-3xl p-8 text-center border border-slate-100">
            <Calendar className="w-12 h-12 text-slate-200 mx-auto mb-3" />
            <p className="text-slate-500 font-medium">Nessuna lezione</p>
            <p className="text-slate-400 text-sm mt-1">Premi + per aggiungere una lezione</p>
          </div>
        ) : (
          Object.entries(groupByDate()).map(([date, lessons]) => (
            <div key={date}>
              {viewMode === 'week' && (
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 capitalize">
                  {formatDate(date)}
                </p>
              )}
              <div className="space-y-2">
                {lessons.map(b => (
                  <LessonCard
                    key={b.booking_id}
                    booking={b}
                    onTogglePaid={() => togglePaid(b)}
                    onToggleCompleted={() => toggleCompleted(b)}
                    onEdit={() => openEditForm(b)}
                    onDelete={() => deleteLesson(b)}
                  />
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* FAB - Add lesson */}
      <button
        onClick={() => openAddForm()}
        data-testid="add-manual-lesson"
        className="fixed bottom-24 sm:bottom-20 right-4 w-14 h-14 bg-teal-600 text-white rounded-2xl shadow-lg flex items-center justify-center hover:bg-teal-700 transition-colors"
        style={{ zIndex: 10001 }}
      >
        <Plus className="w-6 h-6" />
      </button>

      {/* Add/Edit form modal */}
      {showAddForm && (
        <LessonForm
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleSubmit}
          onClose={() => setShowAddForm(false)}
          isEdit={!!editingId}
        />
      )}
    </div>
  );
};

const LessonCard = ({ booking, onTogglePaid, onToggleCompleted, onEdit, onDelete }) => {
  const isManual = booking.is_manual;
  const isPaid = booking.is_paid;
  const isCompleted = booking.is_completed;

  return (
    <div className={`bg-white rounded-2xl border overflow-hidden transition-all ${isCompleted ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-100'}`}
      data-testid={`lesson-${booking.booking_id}`}>
      <div className="p-3.5">
        <div className="flex items-start gap-3">
          {/* Time */}
          <div className="flex-shrink-0 text-center min-w-[50px]">
            <p className="text-lg font-bold text-slate-900 font-mono">{booking.time}</p>
            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${isManual ? 'bg-amber-100 text-amber-700' : 'bg-sky-100 text-sky-700'}`}>
              {isManual ? 'Manuale' : 'App'}
            </span>
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-slate-900 truncate">{booking.player_name || 'Senza nome'}</p>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className="text-xs text-slate-500">{booking.label}</span>
              <span className="text-xs font-bold text-slate-700">{booking.price}€</span>
              {booking.is_recurring && (
                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 flex items-center gap-0.5">
                  <Repeat className="w-2.5 h-2.5" />Fissa
                </span>
              )}
              {booking.notes && <span className="text-xs text-slate-400 truncate max-w-[100px]">{booking.notes}</span>}
            </div>
          </div>

          {/* Price badge */}
          <div className={`flex-shrink-0 px-3 py-1.5 rounded-xl text-sm font-bold ${isPaid ? 'bg-emerald-100 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
            {booking.price}€
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-slate-100">
          <button onClick={onToggleCompleted} data-testid={`toggle-completed-${booking.booking_id}`}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold transition-all ${
              isCompleted ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-50 text-slate-500 hover:bg-slate-100'
            }`}>
            {isCompleted ? <CheckCircle className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />}
            {isCompleted ? 'Completata' : 'Da fare'}
          </button>
          <button onClick={onTogglePaid} data-testid={`toggle-paid-${booking.booking_id}`}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold transition-all ${
              isPaid ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-50 text-amber-700 hover:bg-amber-100'
            }`}>
            {isPaid ? <CheckCircle className="w-3.5 h-3.5" /> : <DollarSign className="w-3.5 h-3.5" />}
            {isPaid ? 'Pagata' : 'Non pagata'}
          </button>
          <button onClick={onEdit} data-testid={`edit-${booking.booking_id}`}
            className="w-9 h-9 flex items-center justify-center rounded-xl bg-slate-50 text-slate-400 hover:bg-sky-50 hover:text-sky-500 transition-colors">
            <Edit3 className="w-3.5 h-3.5" />
          </button>
          <button onClick={onDelete} data-testid={`delete-${booking.booking_id}`}
            className="w-9 h-9 flex items-center justify-center rounded-xl bg-slate-50 text-slate-400 hover:bg-red-50 hover:text-red-500 transition-colors">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

const LessonForm = ({ formData, setFormData, onSubmit, onClose, isEdit }) => {
  const updateField = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const onTypeChange = (num) => {
    const type = LESSON_TYPES.find(t => t.value === num);
    updateField('num_players', num);
    if (type) updateField('price', type.defaultPrice);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
      <div className="relative bg-white w-full sm:w-[420px] max-h-[90vh] rounded-t-3xl sm:rounded-3xl overflow-y-auto animate-slideUp"
        onClick={e => e.stopPropagation()} data-testid="lesson-form-modal">

        <div className="sticky top-0 bg-white border-b border-slate-100 px-5 py-4 flex items-center justify-between rounded-t-3xl z-10">
          <h3 className="font-outfit text-lg font-bold text-slate-900">
            {isEdit ? 'Modifica Lezione' : 'Nuova Lezione'}
          </h3>
          <button onClick={onClose} className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Player name */}
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Nome Allievo</label>
            <div className="relative">
              <User className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={formData.player_name}
                onChange={e => updateField('player_name', e.target.value)}
                data-testid="input-player-name"
                placeholder="Es. Marco Rossi"
                className="w-full pl-10 pr-4 py-3 bg-slate-50 rounded-xl border border-slate-200 focus:border-teal-400 focus:outline-none text-sm"
              />
            </div>
          </div>

          {/* Date & Time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Data</label>
              <div className="relative">
                <Calendar className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input
                  type="date"
                  value={formData.date}
                  onChange={e => updateField('date', e.target.value)}
                  data-testid="input-date"
                  className="w-full pl-10 pr-3 py-3 bg-slate-50 rounded-xl border border-slate-200 focus:border-teal-400 focus:outline-none text-sm"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Orario</label>
              <div className="relative">
                <Clock className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <select
                  value={formData.time}
                  onChange={e => updateField('time', e.target.value)}
                  data-testid="input-time"
                  className="w-full pl-10 pr-3 py-3 bg-slate-50 rounded-xl border border-slate-200 focus:border-teal-400 focus:outline-none text-sm appearance-none"
                >
                  {HOURS.map(h => <option key={h} value={h}>{h}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Lesson type */}
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Tipo Lezione</label>
            <div className="grid grid-cols-4 gap-2">
              {LESSON_TYPES.map(t => (
                <button key={t.value} onClick={() => onTypeChange(t.value)} data-testid={`type-${t.value}`}
                  className={`py-2.5 rounded-xl text-xs font-semibold transition-all ${
                    formData.num_players === t.value
                      ? 'bg-teal-600 text-white shadow-md' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                  }`}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Price */}
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Prezzo (€)</label>
            <div className="relative">
              <DollarSign className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                type="number"
                value={formData.price}
                onChange={e => updateField('price', parseFloat(e.target.value) || 0)}
                data-testid="input-price"
                className="w-full pl-10 pr-4 py-3 bg-slate-50 rounded-xl border border-slate-200 focus:border-teal-400 focus:outline-none text-sm"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Note (opzionale)</label>
            <div className="relative">
              <PenLine className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
              <textarea
                value={formData.notes}
                onChange={e => updateField('notes', e.target.value)}
                data-testid="input-notes"
                placeholder="Es. Prenotato via WhatsApp"
                rows={2}
                className="w-full pl-10 pr-4 py-3 bg-slate-50 rounded-xl border border-slate-200 focus:border-teal-400 focus:outline-none text-sm resize-none"
              />
            </div>
          </div>

          {/* Recurring toggle - only for new lessons */}
          {!isEdit && (
            <div className="bg-purple-50 rounded-2xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <Repeat className="w-5 h-5 text-purple-600" />
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Lezione Fissa Settimanale</p>
                    <p className="text-xs text-slate-500">Ripeti ogni settimana stesso giorno e ora</p>
                  </div>
                </div>
                <button
                  onClick={() => updateField('is_recurring', !formData.is_recurring)}
                  data-testid="toggle-recurring"
                  className={`w-12 h-7 rounded-full transition-all relative ${formData.is_recurring ? 'bg-purple-500' : 'bg-slate-200'}`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full absolute top-1 transition-all shadow-sm ${formData.is_recurring ? 'right-1' : 'left-1'}`} />
                </button>
              </div>
              {formData.is_recurring && (
                <div className="mt-3 flex items-center gap-2">
                  <span className="text-xs text-slate-600">Per le prossime</span>
                  <select
                    value={formData.recurring_weeks}
                    onChange={e => updateField('recurring_weeks', parseInt(e.target.value))}
                    data-testid="input-recurring-weeks"
                    className="px-2 py-1.5 bg-white rounded-lg border border-purple-200 text-sm font-medium text-purple-700 focus:outline-none"
                  >
                    {[4,8,12,16,20,24].map(w => (
                      <option key={w} value={w}>{w} settimane</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          {/* Submit */}
          <button
            onClick={onSubmit}
            data-testid="submit-lesson"
            className="w-full py-3.5 bg-teal-600 text-white rounded-2xl font-semibold hover:bg-teal-700 transition-colors flex items-center justify-center gap-2"
          >
            <CheckCircle className="w-5 h-5" />
            {isEdit ? 'Salva Modifiche' : 'Aggiungi Lezione'}
          </button>
        </div>
      </div>
    </div>
  );
};

const EarningsCard = ({ label, data, accent }) => {
  const colors = {
    sky: { bg: 'bg-sky-50', text: 'text-sky-700', sub: 'text-sky-500', badge: 'bg-sky-100' },
    purple: { bg: 'bg-purple-50', text: 'text-purple-700', sub: 'text-purple-500', badge: 'bg-purple-100' },
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', sub: 'text-emerald-500', badge: 'bg-emerald-100' },
    amber: { bg: 'bg-amber-50', text: 'text-amber-700', sub: 'text-amber-500', badge: 'bg-amber-100' },
  };
  const c = colors[accent] || colors.sky;
  if (!data) return null;
  return (
    <div className={`${c.bg} rounded-2xl p-3.5`} data-testid={`earnings-${label.toLowerCase()}`}>
      <p className={`text-xs font-medium ${c.sub} mb-1`}>{label}</p>
      <p className={`text-xl font-bold ${c.text}`}>{data.my_share}€</p>
      <div className="flex items-center gap-1 mt-1">
        <span className={`text-[10px] ${c.sub}`}>{data.lessons} lez.</span>
        <span className="text-[10px] text-slate-300">|</span>
        <span className="text-[10px] text-slate-400">tot {data.total}€</span>
      </div>
      {data.my_share_unpaid > 0 && (
        <p className="text-[10px] text-amber-600 mt-1 font-medium">{data.my_share_unpaid}€ da incassare</p>
      )}
    </div>
  );
};

export default GestionaleLessonsPage;
